import { ComponentType, ReactNode } from "react";
import { Link } from "react-router-dom";
import useStatistics from "datasets/hooks/useStatistics";
import Dataset from "datasets/types/interfaces/dataset";
import { formatDistanceToNowStrict } from "date-fns";
import { Clock, HardDrive, LucideIcon, Table2 } from "lucide-react";
import pluralize from "pluralize";
import prettyBytes from "pretty-bytes";
import { CardIllustration } from "~/assets/icons";
import { Skeleton } from "~/components/ui/skeleton";
import { type ViewMode } from "~/hooks/useViewMode";
import { cn } from "~/lib/utils";
import { nextGenDatasetDetailPath } from "~/router/constants";
import { AccessLevelBadge } from "./AccessLevelBadge";

interface StatProps {
  icon: LucideIcon | ComponentType<{ className?: string }>;
  children: ReactNode;
  loading?: boolean;
}

const Stat = ({ icon: Icon, children, loading }: StatProps) => {
  return (
    <span className="flex items-center gap-1 text-xs">
      <Icon className="size-3.5" />
      {loading ? <Skeleton className="h-3 w-8" /> : children}
    </span>
  );
};

interface DatasetCardProps {
  dataset: Dataset;
  view?: ViewMode;
}

export const DatasetCard = ({ dataset, view = "card" }: DatasetCardProps) => {
  const { data: stats, isLoading } = useStatistics(dataset.id);
  const updatedAt = dataset.updated_at
    ? formatDistanceToNowStrict(new Date(dataset.updated_at), { addSuffix: true })
    : null;

  const statsContent = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground">
      <Stat icon={Table2}>
        {`${dataset.table_count} ${pluralize("Table", dataset.table_count)}`}
      </Stat>
      <Stat icon={HardDrive} loading={isLoading}>
        {prettyBytes(stats?.table_total_bytes ?? 0)}
      </Stat>
      {updatedAt && <Stat icon={Clock}>{updatedAt}</Stat>}
    </div>
  );

  if (view === "list") {
    return (
      <Link
        to={nextGenDatasetDetailPath(dataset.id)}
        data-testid="nextgen-dataset-card"
        className="group relative block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div
          className={cn(
            "flex items-center gap-4 rounded-lg bg-card px-4 py-5",
            "transition-colors duration-150 group-hover:bg-muted"
          )}
        >
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-semibold text-foreground">{dataset.title}</h3>
            <p
              className={cn(
                "truncate text-xs text-muted-foreground",
                !dataset.description && "italic text-muted-foreground/60"
              )}
            >
              {dataset.description || "No description"}
            </p>
          </div>
          <div className="w-24 shrink-0">
            <AccessLevelBadge accessLevel={dataset.access_level} />
          </div>
          <div className="flex min-w-0 justify-end">{statsContent}</div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={nextGenDatasetDetailPath(dataset.id)}
      data-testid="nextgen-dataset-card"
      className="group relative block h-full rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-transform duration-200 ease-[cubic-bezier(0.165,0.84,0.44,1)] hover:scale-[1.02]"
    >
      <article
        className={cn(
          "relative flex h-full flex-col rounded-lg border border-border bg-card",
          "shadow-[0_1px_2px_rgba(15,23,42,0.03)]"
        )}
      >
        <div
          className="absolute inset-x-0 -top-px h-10 rounded-t-lg bg-[var(--card-header-bg)]"
          aria-hidden="true"
        />
        <div
          className={cn(
            "relative mt-[26px] flex flex-1 flex-col gap-3",
            "rounded-lg rounded-tl-none bg-card px-6 pb-3 pt-4"
          )}
        >
          <CardIllustration
            aria-hidden="true"
            className="pointer-events-none absolute -top-4 left-0 z-10 h-[29px] w-[95px] text-card"
          />
          <h3 className="line-clamp-1 text-base font-semibold leading-tight text-foreground">
            {dataset.title}
          </h3>
          <p
            className={cn(
              "line-clamp-4 text-sm leading-snug text-muted-foreground",
              !dataset.description && "italic text-muted-foreground/60"
            )}
          >
            {dataset.description || "No description"}
          </p>
          <div className="mt-auto flex flex-wrap items-center justify-between gap-2">
            <AccessLevelBadge accessLevel={dataset.access_level} />
            {statsContent}
          </div>
        </div>
      </article>
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-0 rounded-lg",
          "opacity-0 shadow-[0_4px_10px_rgba(15,23,42,0.06)]",
          "transition-opacity duration-200 ease-[cubic-bezier(0.165,0.84,0.44,1)] group-hover:opacity-100"
        )}
      />
    </Link>
  );
};
